import {
  useId,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
} from "react";
import Check from "~icons/ph/check";
import { avatarGenders, avatars } from "../../shared/schemas";
import type { Avatar as AvatarId, Person } from "../../shared/types";
import { Avatar } from "../ui/components";
import { number, t } from "../i18n";

type Gender = (typeof avatarGenders)[number];
const tabLabels: Record<Gender, string> = { male: "ذكر", female: "أنثى" };

export function AvatarPicker({
  person,
  disabled,
  error,
  onSave,
}: {
  person: Person;
  disabled: boolean;
  error: string;
  onSave: (avatar: AvatarId | null) => void;
}) {
  const [choice, setChoice] = useState(person.avatar ?? null);
  const [gender, setGender] = useState<Gender>(
    () =>
      avatarGenders.find((tab) => person.avatar?.startsWith(`${tab}-`)) ??
      "male",
  );
  const id = useId();
  const tabs = useRef<Partial<Record<Gender, HTMLButtonElement | null>>>({});
  function submit(event: FormEvent) {
    event.preventDefault();
    onSave(choice);
  }
  function moveTab(event: KeyboardEvent) {
    const index = avatarGenders.indexOf(gender);
    // With two tabs either arrow switches, so RTL needs no mirroring.
    const next = (
      {
        ArrowLeft: index - 1,
        ArrowRight: index + 1,
        Home: 0,
        End: avatarGenders.length - 1,
      } as Record<string, number>
    )[event.key];
    if (next === undefined) return;
    event.preventDefault();
    const tab =
      avatarGenders[(next + avatarGenders.length) % avatarGenders.length]!;
    setGender(tab);
    tabs.current[tab]?.focus();
  }
  return (
    <>
      <h2>{t("صورة الحساب")}</h2>
      <p className="modal-description">
        {t("اختر صورة يراها أصدقاؤك بجانب اسمك.")}
      </p>
      <form onSubmit={submit}>
        <div
          className="filter-tabs"
          role="tablist"
          aria-label={t("صورة الحساب")}
        >
          {avatarGenders.map((tab) => (
            <button
              key={tab}
              ref={(button) => {
                tabs.current[tab] = button;
              }}
              type="button"
              role="tab"
              id={`${id}-${tab}`}
              aria-controls={`${id}-panel`}
              aria-selected={gender === tab}
              tabIndex={gender === tab ? 0 : -1}
              className={gender === tab ? "selected" : ""}
              onClick={() => setGender(tab)}
              onKeyDown={moveTab}
            >
              {t(tabLabels[tab])}
            </button>
          ))}
        </div>
        <div
          className="avatar-picker"
          role="tabpanel"
          id={`${id}-panel`}
          aria-labelledby={`${id}-${gender}`}
        >
          {[
            null,
            ...avatars.filter((avatar) => avatar.startsWith(`${gender}-`)),
          ].map((avatar, index) => {
            const label = avatar
              ? t("صورة {number}", { number: number(index) })
              : t("الأحرف الأولى");
            return (
              <label key={avatar ?? "initials"} title={label}>
                <input
                  className="sr-only"
                  type="radio"
                  name="avatar"
                  aria-label={label}
                  checked={choice === avatar}
                  onChange={() => setChoice(avatar)}
                />
                <Avatar person={{ ...person, avatar }} large />
              </label>
            );
          })}
        </div>
        {error && (
          <p className="form-error" role="alert">
            {t(error)}
          </p>
        )}
        <button className="button full" disabled={disabled}>
          {t("حفظ التغييرات")} <Check width={16} height={16} />
        </button>
      </form>
    </>
  );
}
